/**
 * React hook wrapping `tacticsPatternsStore` — provides patterns,
 * a refresh trigger, save / remove helpers, and a loading flag.
 *
 * Shape mirrors the project's other query hooks (`useTeamSessions`,
 * `useDrillLibrary`): a single result object the surface can spread
 * into UI without juggling refs or contexts. The store itself is
 * localStorage-backed today; when v2 swaps in the Prisma
 * `tactics_patterns` table this hook absorbs the change without the
 * caller's signature moving.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  deletePattern,
  listPatterns,
  savePattern,
  type ListPatternsFilter,
  type SavePatternInput,
  type TacticsPattern,
} from '../tacticsPatternsStore';
import { getFamousTacticsPatterns } from '../famousTactics';

/** Filter a TacticsPattern list against the same fields `listPatterns`
 *  applies — kept inline so the famous-library merge can share the
 *  filter semantics without duplicating store internals. */
function applyFilter(
  rows: TacticsPattern[],
  filter: ListPatternsFilter,
): TacticsPattern[] {
  let out = rows;
  if (filter.tags && filter.tags.length > 0) {
    const wanted = filter.tags.map((t) => t.toLowerCase());
    out = out.filter((p) => {
      const pTags = p.tags.map((t) => t.toLowerCase());
      return wanted.every((w) => pTags.includes(w));
    });
  }
  if (filter.phaseOfPlay) {
    out = out.filter((p) => p.phaseOfPlay === filter.phaseOfPlay);
  }
  if (filter.formation) {
    out = out.filter((p) => p.formation === filter.formation);
  }
  if (filter.limit && filter.limit > 0) {
    out = out.slice(0, filter.limit);
  }
  return out;
}

interface UseTacticsPatternsOptions {
  /** Filter applied to listPatterns. Stable identity preferred (memoise upstream). */
  filter?: ListPatternsFilter;
}

interface UseTacticsPatternsResult {
  patterns: TacticsPattern[];
  loading: boolean;
  refresh: () => void;
  save: (input: SavePatternInput) => TacticsPattern;
  remove: (id: string) => void;
}

/**
 * Hook returning the filtered pattern library. `refresh` is a manual
 * re-fetch hook for surfaces that write through the store directly
 * (e.g. the Brain commit pipeline) and need the drawer to pick up the
 * new row without remounting.
 */
export function useTacticsPatterns(
  options: UseTacticsPatternsOptions = {},
): UseTacticsPatternsResult {
  // Stable filter identity — callers can pass an inline object on every
  // render without spinning the effect; we hash on the explicit fields
  // we care about.
  const { filter } = options;
  const tagsKey = filter?.tags ? filter.tags.join('|') : '';
  const phase = filter?.phaseOfPlay ?? '';
  const formation = filter?.formation ?? '';
  const limit = filter?.limit ?? 0;

  const [patterns, setPatterns] = useState<TacticsPattern[]>([]);
  const [loading, setLoading] = useState(true);
  // Manual refresh signal — bumped to re-trigger the load effect.
  const [tick, setTick] = useState(0);

  const effectiveFilter = useMemo<ListPatternsFilter>(() => {
    const f: ListPatternsFilter = {};
    if (filter?.tags && filter.tags.length > 0) f.tags = filter.tags;
    if (filter?.phaseOfPlay) f.phaseOfPlay = filter.phaseOfPlay;
    if (filter?.formation) f.formation = filter.formation;
    if (filter?.limit && filter.limit > 0) f.limit = filter.limit;
    return f;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on stable strings above
  }, [tagsKey, phase, formation, limit]);

  useEffect(() => {
    setLoading(true);
    // listPatterns is synchronous (localStorage read), but we still set
    // `loading: true` then `false` on the same tick so callers can
    // render a skeleton if they want — the drawer's empty state vs the
    // "no patterns yet" state differ visually.
    const saved = listPatterns(effectiveFilter);
    const famous = applyFilter(getFamousTacticsPatterns(), effectiveFilter);
    // Famous patterns appear first — broadcast / coaching-literature
    // entry points before the coach's own library. The drawer's
    // existing PatternThumbnail already surfaces `sourceType` so the
    // `CURATED_EXPERT` tag distinguishes them visually.
    setPatterns([...famous, ...saved]);
    setLoading(false);
  }, [effectiveFilter, tick]);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  const save = useCallback(
    (input: SavePatternInput): TacticsPattern => {
      const saved = savePattern(input);
      // Refresh after a write so the caller's `patterns` reflects the
      // new row immediately without an extra setTick from upstream.
      setTick((t) => t + 1);
      return saved;
    },
    [],
  );

  const remove = useCallback((id: string) => {
    // Famous-library entries are read-only — `deletePattern` would
    // no-op on an unknown id, but short-circuiting is clearer + saves
    // a store round-trip.
    if (id.startsWith('famous-')) return;
    deletePattern(id);
    setTick((t) => t + 1);
  }, []);

  return { patterns, loading, refresh, save, remove };
}
